import * as React from 'react';
import { FC, useCallback, useEffect } from 'react';
import { Modal } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { stepMediaIndex } from './NotesMediaDeck';
import { useHeicSafeSrc } from '../../hooks/useHeicSafeSrc';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { dateFormatterWithoutTimeZone } from '../../utils/DataFormatterUtils';
import {
	getNoteMediaAuthorName,
	NOTE_MEDIA_DATE_FORMAT,
	NOTE_MEDIA_TYPE_VIDEO,
	NoteMediaItem,
} from '../../utils/NoteMediaUtils';

export const getMediaCaption = (item: NoteMediaItem): string =>
	`${getNoteMediaAuthorName(item)} • ${dateFormatterWithoutTimeZone(
		item.addedAt,
		NOTE_MEDIA_DATE_FORMAT
	)}`;

const ARROW_CLASS_NAME =
	'absolute top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center ' +
	'justify-center rounded-full border-0 bg-black/45 text-white hover:bg-black/60';

interface NotesMediaViewerProps {
	/** Photos and videos interleaved, in the order they were shared — the whole deck. */
	items: NoteMediaItem[];
	/** Which item is on screen. */
	index: number;
	onIndexChange: (index: number) => void;
	onClose: () => void;
}

/**
 * The full-size view of one photo or video from the media panel, with the rest of the conversation's
 * media a step away on either side. Photos and videos share the one deck — a video is a stop on the
 * way to the next photo rather than something the arrows skip over — which is why this replaces
 * antd's `Image.PreviewGroup` here: a preview group can only hold images.
 */
const NotesMediaViewer: FC<NotesMediaViewerProps> = ({ items, index, onIndexChange, onClose }) => {
	const item = items[index];
	// Hooks can't be called conditionally, and a video's URL is simply not HEIC, so it passes
	// straight through.
	const safeSrc = useHeicSafeSrc(item ? item.url : '');

	const step = useCallback(
		(delta: number) => {
			const nextIndex = stepMediaIndex(index, delta, items.length);
			if (nextIndex !== index) {
				onIndexChange(nextIndex);
			}
		},
		[index, items.length, onIndexChange]
	);

	// The arrow keys move through the deck, the way they do in antd's own preview. Esc is left to
	// the Modal, which already closes on it.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowLeft') {
				step(-1);
			}
			if (event.key === 'ArrowRight') {
				step(1);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [step]);

	if (!item) {
		return null;
	}

	const handlePrevious = () => step(-1);
	const handleNext = () => step(1);

	return (
		<Modal
			visible={true}
			width={960}
			centered
			footer={null}
			title={getMediaCaption(item)}
			onCancel={onClose}
		>
			<div className="relative flex items-center justify-center">
				{item.mediaType === NOTE_MEDIA_TYPE_VIDEO ? (
					// Keyed by URL so stepping to another video mounts a new element rather than leaving the
					// previous one playing under a swapped `src`.
					<video key={item.url} width="100%" height="auto" controls autoPlay>
						<source src={item.url} type={getFileMimeTypeFromString(item.url)} />
						Your browser does not support the video tag.
					</video>
				) : (
					<img src={safeSrc} alt="" className="max-h-[70vh] w-full object-contain" />
				)}
				{index > 0 ? (
					<button
						type="button"
						aria-label="Previous"
						className={`${ARROW_CLASS_NAME} left-2`}
						onClick={handlePrevious}
					>
						<LeftOutlined />
					</button>
				) : null}
				{index < items.length - 1 ? (
					<button
						type="button"
						aria-label="Next"
						className={`${ARROW_CLASS_NAME} right-2`}
						onClick={handleNext}
					>
						<RightOutlined />
					</button>
				) : null}
			</div>
			<div className="footnoteregular mt-3 text-center text-character-secondary">
				{index + 1} / {items.length}
			</div>
		</Modal>
	);
};

export default NotesMediaViewer;
